import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/shared/components/ui/dropdown-menu";
import { Button } from "@/shared/components/ui/button";
import { useTranslation } from "react-i18next";
import { MoreVertical, Info, X } from "lucide-react";
import { ScheduleService } from "@/features/Assistant/services/scheduleService";
import type { ActiveCall } from "@/features/Assistant/types/schedule";

interface ActiveCallsTableProps {
  calls: ActiveCall[];
  totalPages: number;
  page: number;
  setPage: (p: number) => void;
  rowsPerPage: string;
  setRowsPerPage: (v: string) => void;
  onDetails: (call: ActiveCall) => void;
  onForceLeave: (callId: string) => void;
}

export const ActiveCallsTable = ({
  calls,
  totalPages,
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  onDetails,
  onForceLeave,
}: ActiveCallsTableProps) => {
  const { t } = useTranslation();

  if (calls.length === 0) return null;

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold flex items-center gap-2">
        <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
        {t("assistant.schedule.activeCallsTitle")}
      </h3>

      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead className="text-xs uppercase">{t("assistant.schedule.columns.callId")}</TableHead>
              <TableHead className="text-xs uppercase">{t("assistant.schedule.columns.meeting")}</TableHead>
              <TableHead className="text-xs uppercase">{t("assistant.schedule.columns.startTime")}</TableHead>
              <TableHead className="text-xs uppercase text-right">{t("assistant.schedule.columns.actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {calls.map((call) => (
              <TableRow key={call.callId} className="hover:bg-muted/50">
                <TableCell className="text-xs font-mono text-muted-foreground">{call.callId}</TableCell>
                <TableCell className="text-sm font-medium">{call.meetingName || '—'}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{ScheduleService.formatTime(call.startTime ?? '')}</TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => onDetails(call)}>
                        <Info className="mr-2 h-4 w-4" />
                        {t("assistant.schedule.actions.details")}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => onForceLeave(call.callId)}
                        className="text-red-500 focus:text-red-500 data-[highlighted]:text-red-500 data-[highlighted]:bg-red-500/10 font-medium cursor-pointer"
                      >
                        <X className="mr-2 h-4 w-4 !opacity-100 text-red-500" />
                        {t("assistant.schedule.actions.forceLeave")}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <div className="flex items-center justify-between px-4 py-2 border-t text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            <span>{t("assistant.schedule.rowsPerPage")}</span>
            <Select value={rowsPerPage} onValueChange={(value) => { setRowsPerPage(value); setPage(1); }}>
              <SelectTrigger className="w-16 h-7">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1} className="h-7 w-7 p-0">
              ‹
            </Button>
            <span>{t("assistant.schedule.pagination", { current: page, total: totalPages })}</span>
            <Button variant="outline" size="sm" onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page >= totalPages} className="h-7 w-7 p-0">
              ›
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
